import { ReactNode, useMemo, useState } from "react";
import SaveGuardEnabledIdsContext from "@coworker/apprestructured/src/save-guard/SaveGuardProvider/SaveGuardEnabledIdsContext";
import SaveGuardPrompt from "@coworker/apprestructured/src/save-guard/SaveGuardProvider/SaveGuardPrompt/SaveGuardPrompt";
import SaveGuardPromptHandlersContext from "@coworker/apprestructured/src/save-guard/SaveGuardProvider/SaveGuardPromptHandlersContext";
import useInternalNavigationBlocker from "@coworker/apprestructured/src/save-guard/SaveGuardProvider/useInternalNavigationBlocker";
import useExternalNavigationBlocker from "@coworker/apprestructured/src/save-guard/SaveGuardProvider/useExternalNavigationBlocker";
import useStayAndLeaveHandlers from "@coworker/apprestructured/src/save-guard/SaveGuardProvider/useStayAndLeaveHandlers";

const noop = () => {};

type SaveGuardProps = {
  children: ReactNode;
};

/**
 * Wrap your app or test in this component to enable save guard functionality.
 */
function SaveGuardProvider({ children }: SaveGuardProps) {
  /**
   * A save guard is considered 'enabled' when its id is in this set.
   */
  const [saveGuardEnabledIds, setSaveGuardEnabledIds] = useState(
    new Set<string>()
  );

  /**
   * State and setters for the save guard prompt actions.
   */
  const { stayHandler, setStayHandler, leaveHandler, setLeaveHandler } =
    useStayAndLeaveHandlers();

  const saveGuardPromptHandlersContextValue = useMemo(() => {
    return {
      setStayHandler,
      setLeaveHandler,
    };
  }, [setStayHandler, setLeaveHandler]);

  useInternalNavigationBlocker(
    saveGuardEnabledIds,
    setSaveGuardEnabledIds,
    setLeaveHandler,
    setStayHandler
  );

  useExternalNavigationBlocker(saveGuardEnabledIds);

  const saveGuardPrompt = useMemo(() => {
    return (
      <SaveGuardPrompt
        visible={!!stayHandler && !!leaveHandler}
        onStay={stayHandler ?? noop}
        onLeave={leaveHandler ?? noop}
      />
    );
  }, [leaveHandler, stayHandler]);

  return (
    <>
      <SaveGuardEnabledIdsContext
        value={[saveGuardEnabledIds, setSaveGuardEnabledIds]}
      >
        <SaveGuardPromptHandlersContext
          value={saveGuardPromptHandlersContextValue}
        >
          {children}
        </SaveGuardPromptHandlersContext>
      </SaveGuardEnabledIdsContext>
      {saveGuardPrompt}
    </>
  );
}

export default SaveGuardProvider;
